/**
 * @desc: CasualGamesHub/RobloxGameRank 页面
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { useIntl, useLocation } from 'umi';
import { Card, Justify } from 'tea-component';
import '@intelligence/v3/filterControls';
import Filter from '@intelligence/v3/components/Filter/index';
import CardSlot from '@intelligence/components/CardSlot';
import SearchRecent from '@intelligence/components/SearchRecent';
import ReusePageTemplate from '@intelligence/v3/components/ReusePageTemplate';
import RobloxPageTitle from '@intelligence/v3/components/RobloxPageTitle';
import useFilterablePage from '@intelligence/v3/_hooks/useFilterablePage';
import useRobloxFilterablePageBoot from '@intelligence/v3/_hooks/useRobloxFilterablePageBoot';
import useMobileStoreRankRegionOptions from '@intelligence/v3/_hooks/useMobileStoreRankRegionOptions';
import useGenreSourcesOptions from '@intelligence/components/GenreSelectMiddleware/_hooks/useGenreSourcesOptions';
import { GENRE_SOURCE_MAP } from '@intelligence/components/GenreSelect/constants';
import { fetchRobloxGameRanks } from '@intelligence/v3/services/robloxGameRank';
import RobloxGameRankTable from './components/RobloxGameRankTable';
import RobloxGameRankSectionTitle from './components/RobloxGameRankSectionTitle';
import RobloxGameRankLineChart from './components/RobloxGameRankLineChart';
import RobloxGameRankDownload from './components/RobloxGameRankDownload';
import useRobloxGameRankTable from './hooks/useRobloxGameRankTable';
import useRobloxGameRankChart from './hooks/useRobloxGameRankChart';
import usePageLog from '@intelligence/v3/_hooks/usePageLog';
import {
  getRobloxGameRankPageLogKey,
  getRobloxGameRankSearchPageLogInfo,
  reportRobloxGameRankSourceChangeLog,
} from './config/pageLogConfig';
import {
  FILTER_SAVE_TYPE,
  PAGE_ID,
  ROBLOX_SEARCH_DROPDOWN_PAGE_SIZE,
  getDefaultFilterValues,
} from './constants';
import {
  ROBLOX_GAME_RANK_DEFAULT_CATEGORY,
  ROBLOX_GAME_RANK_DEFAULT_SOURCE,
  getDefaultSourceByCategory,
} from './config/robloxRankSourceConfig';
import { getRobloxGameRankFilterItems } from './filterConfig';
import { buildRobloxGameRankFetchParams } from './utils/buildRobloxGameRankFetchParams';
import { getRangePickerValue } from '@intelligence/v3/filterControls/rangePicker/utils';
import { transRobloxTableDataForSearch } from './utils/transRobloxSearchData';
import {
  mergeRobloxFilterValuesWithResolvedDateRange,
} from '@intelligence/v3/utils/robloxFilterDate';
import type { IRobloxResolvedDateRange } from '@intelligence/v3/utils/robloxFilterDate';
import type { IRobloxGameRankFilterValues } from './types';
import type { IMainGenre } from '@intelligence/types/common';
import type { TRobloxRankCategory } from './config/robloxRankSourceConfig';
import type { TableSelectionType, TableSelectStatusType } from '@intelligence/components/TableComponents/components/TableCheckbox';
import type { SortByItemType } from '@intelligence/components/TableComponents/components/SortBy';
import './index.scss';

interface IRobloxGameRankProps extends IBaseProps {}

const RobloxGameRank: React.FC<IRobloxGameRankProps> = (props) => {
  const { style, className } = props;
  const { formatMessage } = useIntl();
  const location = useLocation();
  const filterValuesRef = useRef<IRobloxGameRankFilterValues>(getDefaultFilterValues());
  const robloxGenreOptionsRef = useRef<IMainGenre[]>([]);

  const defaultValues = useMemo(() => getDefaultFilterValues(), []);
  const { isLoading: regionOptionsLoading, regionOptions } = useMobileStoreRankRegionOptions();
  const { genres, options: genreOptions, loading: genreOptionsLoading } = useGenreSourcesOptions({
    sources: [GENRE_SOURCE_MAP.ROBLOX],
  });
  robloxGenreOptionsRef.current = genres.roblox || [];
  const [filterValues, setFilterValues] = useState<IRobloxGameRankFilterValues>(defaultValues);
  const [rankCategory, setRankCategory] = useState<TRobloxRankCategory>(ROBLOX_GAME_RANK_DEFAULT_CATEGORY);
  const [rankSource, setRankSource] = useState(ROBLOX_GAME_RANK_DEFAULT_SOURCE);
  const [isCoupledDataLoading, setIsCoupledDataLoading] = useState(false);

  const pageLogKey = getRobloxGameRankPageLogKey(rankCategory);
  usePageLog(pageLogKey);

  const searchPageLogInfo = useMemo(
    () => getRobloxGameRankSearchPageLogInfo(rankCategory),
    [rankCategory],
  );

  const items = useMemo(() => {
    return getRobloxGameRankFilterItems({
      regionOptions: regionOptions || [],
      genreOptions: { roblox: genreOptions.roblox || [] },
      genreOptionsLoading,
    });
  }, [regionOptions, genreOptions.roblox, genreOptionsLoading]);

  const optionsReady = !regionOptionsLoading && !genreOptionsLoading;
  const hookReady = optionsReady;
  const { getInitialFilterValues, syncFilterState } = useFilterablePage({
    pageId: PAGE_ID,
    items,
    defaultValues,
    ready: hookReady,
  });

  const {
    loading: tableLoading,
    records,
    total,
    pageIndex,
    pageSize,
    selectedGameId,
    rankOrder,
    rankSortKey,
    tableSelection,
    tableSelectionAll,
    fetchTable,
    handlePagingChange,
    handleCheckGame,
  } = useRobloxGameRankTable(robloxGenreOptionsRef);

  const {
    loading: chartLoading,
    chartList,
    fetchChart,
    clearChart,
    setChartLoading,
  } = useRobloxGameRankChart(robloxGenreOptionsRef);

  const handleChartFetch = useCallback(async (
    filterValues: IRobloxGameRankFilterValues,
    payload: {
      chartGameIds: string[];
      source: string;
      resolvedDateRange?: IRobloxResolvedDateRange | null;
    },
  ) => {
    if (payload.chartGameIds.length) {
      await fetchChart(filterValues, {
        source: payload.source,
        gameIds: payload.chartGameIds,
        resolvedDateRange: payload.resolvedDateRange,
      });
      return;
    }

    clearChart();
  }, [fetchChart, clearChart]);

  const applyResolvedDateRangeToFilter = useCallback((
    resolvedDateRange: IRobloxResolvedDateRange | null,
  ): IRobloxGameRankFilterValues => {
    const nextFilterValues = mergeRobloxFilterValuesWithResolvedDateRange(
      filterValuesRef.current,
      resolvedDateRange,
    );

    if (!nextFilterValues) {
      return filterValuesRef.current;
    }

    const mergedFilterValues = nextFilterValues as IRobloxGameRankFilterValues;
    setFilterValues(mergedFilterValues);
    filterValuesRef.current = mergedFilterValues;
    syncFilterState(mergedFilterValues);
    return mergedFilterValues;
  }, [syncFilterState]);

  const fetchTableWithChart = useCallback(async (
    filterValues: IRobloxGameRankFilterValues,
    options?: Parameters<typeof fetchTable>[1],
  ) => {
    setIsCoupledDataLoading(true);
    setChartLoading(true);

    try {
      await fetchTable(filterValues, {
        ...options,
        fetchChart: true,
        onAfterFetch: async (payload) => {
          const nextFilterValues = applyResolvedDateRangeToFilter(payload.resolvedDateRange);
          await handleChartFetch(nextFilterValues, payload);
        },
      });
    } finally {
      setIsCoupledDataLoading(false);
    }
  }, [fetchTable, handleChartFetch, setChartLoading, applyResolvedDateRangeToFilter]);

  const { isPageBootLoading, loading } = useRobloxFilterablePageBoot({
    hookReady,
    filterLoading: regionOptionsLoading,
    locationSearch: location.search,
    locationKey: location.key,
    getInitialFilterValues,
    logTag: 'RobloxGameRank',
    onBeforeInitialize: () => {
      setRankCategory(ROBLOX_GAME_RANK_DEFAULT_CATEGORY);
      setRankSource(ROBLOX_GAME_RANK_DEFAULT_SOURCE);
    },
    onInitialized: async (nextFilterValues) => {
      setFilterValues(nextFilterValues);
      filterValuesRef.current = nextFilterValues;
      await fetchTableWithChart(nextFilterValues, {
        source: ROBLOX_GAME_RANK_DEFAULT_SOURCE,
        resetPage: true,
        resetSelection: true,
        gameIds: [],
      });
    },
  });

  const searchFetchParams = useMemo(() => {
    return buildRobloxGameRankFetchParams(filterValues, {
      page: 1,
      pageSize: ROBLOX_SEARCH_DROPDOWN_PAGE_SIZE,
      source: rankSource,
      robloxGenreOptions: robloxGenreOptionsRef.current,
    });
  }, [filterValues, rankSource, genreOptionsLoading]);

  const chartGranularity = useMemo(
    () => getRangePickerValue(filterValues.date ?? getDefaultFilterValues().date).granularity || 'daily',
    [filterValues.date],
  );

  const chartTitle = useMemo(
    () => formatMessage({ id: 'dbi_roblox_game_rank_section_title' }),
    [formatMessage],
  );

  const searchListApi = useCallback((params: any, options?: { signal?: AbortSignal }) => {
    return fetchRobloxGameRanks(params, options);
  }, []);

  const getTableSearchList = useCallback((gameId: string) => {
    fetchTableWithChart(filterValuesRef.current, {
      rankGameIds: [gameId],
      resetPage: true,
      resetSelection: true,
      source: rankSource,
    });
  }, [fetchTableWithChart, rankSource]);

  const handleSearchClear = useCallback(() => {
    fetchTableWithChart(filterValuesRef.current, {
      gameIds: [],
      resetPage: true,
      resetSelection: true,
      source: rankSource,
    });
  }, [fetchTableWithChart, rankSource]);

  const handleRankCategoryChange = useCallback((category: TRobloxRankCategory) => {
    const nextSource = getDefaultSourceByCategory(category);
    setRankCategory(category);
    setRankSource(nextSource);
    fetchTableWithChart(filterValuesRef.current, {
      source: nextSource,
      resetPage: true,
      resetSelection: true,
      gameIds: [],
    });
  }, [fetchTableWithChart]);

  const handleRankSourceChange = useCallback((source: string) => {
    setRankSource(source);
    reportRobloxGameRankSourceChangeLog(rankCategory, source);
    fetchTableWithChart(filterValuesRef.current, {
      source,
      resetPage: true,
      resetSelection: true,
      gameIds: [],
    });
  }, [fetchTableWithChart, rankCategory]);

  const handleTableSortChange = useCallback((sortItem: SortByItemType) => {
    fetchTableWithChart(filterValuesRef.current, {
      rankOrder: sortItem?.sortVal === 'asc' ? 'asc' : 'desc',
      rankSortKey: sortItem?.key,
      gameIds: [],
      resetPage: true,
      resetSelection: false,
      source: rankSource,
    });
  }, [fetchTableWithChart, rankSource]);

  const handleTableCheckGame = useCallback((
    list: TableSelectionType[],
    status: TableSelectStatusType,
  ) => {
    handleCheckGame(list, status);

    const gameIds = list
      .map((item) => String(item?.gameId || ''))
      .filter(Boolean);

    if (gameIds.length) {
      fetchChart(filterValuesRef.current, {
        source: rankSource,
        gameIds,
      });
      return;
    }

    clearChart();
  }, [handleCheckGame, fetchChart, clearChart, rankSource]);

  useEffect(() => {
    filterValuesRef.current = filterValues;
  }, [filterValues]);

  const handleConfirm = (values: IRobloxGameRankFilterValues): void => {
    setFilterValues(values);
    filterValuesRef.current = values;
    syncFilterState(values);
    fetchTableWithChart(values, {
      source: rankSource,
      resetPage: true,
      resetSelection: true,
      gameIds: [],
    });
  };

  const handleReset = (values: IRobloxGameRankFilterValues): void => {
    setFilterValues(values);
    filterValuesRef.current = values;
    fetchTableWithChart(values, {
      source: rankSource,
      resetPage: true,
      resetSelection: true,
      gameIds: [],
    });
  };

  return (
    <ReusePageTemplate
      style={style}
      className={classnames('dbi-reuse-page-roblox-game-rank', className)}
      title={
        <RobloxPageTitle
          title={formatMessage({ id: 'dbi_reuse_page_title_roblox_game_rank' })}
        />
      }
    >
      <div className="dbi-multi-datasource-top-charts-template">
        <Card>
          <Card.Body>
            <Filter
              loading={loading}
              items={items}
              value={filterValues}
              resetValue={defaultValues}
              saveFilterType={FILTER_SAVE_TYPE}
              useActionBar
              useSaveFilter
              useSticky
              stickyPortalContainer=".dbi-multi-datasource-top-charts-template"
              onConfirm={handleConfirm}
              onReset={handleReset}
            />
          </Card.Body>
        </Card>

        <RobloxGameRankSectionTitle
          category={rankCategory}
          source={rankSource}
          onCategoryChange={handleRankCategoryChange}
          onSourceChange={handleRankSourceChange}
          chart={
            <RobloxGameRankLineChart
              loading={chartLoading || isPageBootLoading}
              chartList={chartList}
              tableSelection={tableSelection}
              selectedSource={rankSource}
              chartTitle={chartTitle}
              granularity={chartGranularity}
            />
          }
        />

        <Justify
          className="dbi-card-justify"
          right={
            <>
              <SearchRecent
                pageLogInfo={searchPageLogInfo}
                searchType="roblox"
                searchKey="keyword"
                fetchParams={searchFetchParams}
                initSearchInput={!selectedGameId}
                placeholder={formatMessage({ id: 'dbi_search_any_game' })}
                searchListApi={searchListApi}
                getTableResultList={getTableSearchList}
                transTableDataForSearch={transRobloxTableDataForSearch}
                handleClear={handleSearchClear}
              />
              <RobloxGameRankDownload
                filterValues={filterValues}
                rankCategory={rankCategory}
                rankSource={rankSource}
                tableSelection={tableSelection}
                regionOptions={regionOptions || []}
                total={total}
                robloxGenreOptions={robloxGenreOptionsRef.current}
              />
            </>
          }
        />

        <CardSlot>
          <RobloxGameRankTable
            loading={tableLoading || isCoupledDataLoading || isPageBootLoading}
            records={records}
            total={total}
            pageIndex={pageIndex}
            pageSize={pageSize}
            selectedSource={rankSource}
            rankOrder={rankOrder}
            rankSortKey={rankSortKey}
            tableSelection={tableSelection}
            tableSelectionAll={tableSelectionAll}
            onPagingChange={(paging) => {
              handlePagingChange(filterValuesRef.current, paging);
            }}
            onCheckGame={handleTableCheckGame}
            onSortChange={handleTableSortChange}
            robloxGenreOptions={genres.roblox || []}
          />
        </CardSlot>
      </div>
    </ReusePageTemplate>
  );
};

export default RobloxGameRank;
